import {
  SystemModules,
  PermissionNumber,
} from '@domains/person/user/self/data';
import { PlatformFeature, PLATFORM_FEATURES } from '@application/config';
import { goodsPageFragments } from '@goods/data-access';
import { accessPageFragments } from './access-settings.model';
import { channelsPageFragments } from './channels-settings.model';
import { bookingPageFragments } from './booking.model';
import { CRMPageFragments } from './crm-settings.model';
import { financialPageFragments } from './financial-settings.model';
import { generalPageFragments } from './general-settings.model';
import { hotsitePageFragments } from './hotsite.model';
import { integrationsPageFragments } from './integrations-settings.model';
import { schoolPageFragments } from './school-settings.model';
import { subscriptionsPageFragments } from './subscription-settings.model';
import { treinoPageFragments } from './treino-settings.model';
import { usersPageFragments } from './users-settings.model';

export type SettingsMenuFragment = {
  id: string;
  title: string;
  requiredModules?: SystemModules[];
  /** When set, the fragment is hidden unless every listed platform feature is enabled. */
  requiredPlatformFeatures?: PlatformFeature[];
  /** When set, the fragment is hidden if any listed platform feature is enabled. */
  hiddenPlatformFeatures?: PlatformFeature[];
  /** When true, the fragment is shown only for tenants in `treasuryFullEnabledTenants`. */
  treasuryFullEnabledTenantOnly?: boolean;
  /** When true, the fragment is shown only for users with admin scope (Actuar/Paguela/FacilFit/Toletus admins). */
  adminScopeOnly?: boolean;
  /**
   * When set, the fragment only shows for the matching print flow (localStorage
   * flag `print-agent-flow`). Lets the legacy spooler section and the new
   * print-agent sections coexist without either leaking into the other's tenant.
   */
  printFlow?: 'legacy' | 'agent';
};

export type SettingsMenuItem = {
  label: string;
  route: string;
  pageFragments?: SettingsMenuFragment[];
  /**
   * When true, the whole item is hidden unless the user has admin scope — the
   * item-level twin of `SettingsMenuFragment.adminScopeOnly`.
   *
   * Needed because a section whose every fragment is admin-only would still show
   * its (now empty) entry in both menus. Deep links are gated separately by
   * `AdminGuard` on the route: hiding a menu entry is not access control.
   */
  adminScopeOnly?: boolean;
  requiredModules?: SystemModules[];
  requiredAnyModules?: SystemModules[];
  requiredPlatformFeatures?: PlatformFeature[];
  /**
   * Hides the item unless the user holds every listed permission (the owner
   * always does) — the same test the route's `PermissionGuard` applies, so the
   * menu stops offering a page that would only open behind another user's login.
   */
  requiredPermissions?: PermissionNumber[];
};

/**
 * The permission that opens Ajustes as a whole. Pages whose route declares
 * `skipSectionPermission` (Auditoria, Minha assinatura) open on their own
 * permission instead, so the "Ajustes" entry points must not assume that
 * someone without this one has nowhere to go there.
 */
export const SETTINGS_SECTION_PERMISSIONS: readonly PermissionNumber[] = [
  PermissionNumber.AjusteAtualizar,
];

export const SETTINGS_MENU_ITEMS: readonly SettingsMenuItem[] = [
  {
    label: 'SETTINGS.MENU.GENERAL',
    route: 'general',
    pageFragments: generalPageFragments,
  },
  {
    label: 'SETTINGS.MENU.SCHOOL',
    route: 'school',
    pageFragments: schoolPageFragments,
    requiredPlatformFeatures: [PLATFORM_FEATURES.school],
  },
  {
    label: 'SETTINGS.MENU.SUBSCRIPTIONS',
    route: 'subscriptions',
    pageFragments: subscriptionsPageFragments,
    requiredModules: [SystemModules.Assinaturas],
  },
  {
    label: 'SETTINGS.MENU.GOODS',
    route: 'goods',
    pageFragments: goodsPageFragments,
    requiredModules: [SystemModules.Mercadorias],
    requiredPlatformFeatures: [PLATFORM_FEATURES.goods],
  },
  {
    label: 'SETTINGS.MENU.SERVICES',
    route: 'services',
    requiredModules: [SystemModules.Servicos],
    requiredPlatformFeatures: [PLATFORM_FEATURES.workServices],
  },
  {
    label: 'SETTINGS.MENU.FINANCIAL',
    route: 'financial',
    pageFragments: financialPageFragments,
    requiredAnyModules: [
      SystemModules.Assinaturas,
      SystemModules.Mercadorias,
      SystemModules.Servicos,
    ],
    requiredPermissions: [PermissionNumber.ModuloFinanceiroVisualizar],
  },
  {
    label: 'SETTINGS.MENU.TREI_NO',
    route: 'treino',
    pageFragments: treinoPageFragments,
    requiredModules: [SystemModules.TreiNo],
    requiredPlatformFeatures: [PLATFORM_FEATURES.treino],
  },
  {
    label: 'SETTINGS.MENU.BOOKINGS',
    route: 'bookings',
    pageFragments: bookingPageFragments,
    requiredModules: [SystemModules.Reservas],
    requiredPlatformFeatures: [PLATFORM_FEATURES.bookings],
  },
  {
    label: 'SETTINGS.MENU.ACCESS',
    route: 'access',
    pageFragments: accessPageFragments,
    requiredModules: [SystemModules.Acesso],
  },
  {
    label: 'SETTINGS.MENU.CRM',
    route: 'crm',
    pageFragments: CRMPageFragments,
    requiredModules: [SystemModules.CRM],
  },
  { label: 'HotSite', route: 'hotsite', pageFragments: hotsitePageFragments },
  {
    label: 'SETTINGS.MENU.CONTRACTS',
    route: 'contract',
    requiredAnyModules: [SystemModules.ActuarWeb, SystemModules.Contratos],
  },
  {
    label: 'SETTINGS.MENU.USERS',
    route: 'users',
    pageFragments: usersPageFragments,
  },
  {
    label: 'SETTINGS.MENU.AUDIT',
    route: 'audit',
    requiredPermissions: [PermissionNumber.AuditoriaVisualizar],
  },
  { label: 'SETTINGS.MENU.LOGOS', route: 'logos' },
  {
    label: 'SETTINGS.MENU.INTEGRATIONS',
    route: 'integrations',
    pageFragments: integrationsPageFragments,
    requiredModules: [SystemModules.Integrations],
  },
  {
    label: 'SETTINGS.MENU.WHATSAPP',
    route: 'whatsapp',
    requiredModules: [SystemModules.Whatsapp],
  },
  {
    label: 'SETTINGS.MENU.CHANNELS',
    route: 'channels',
    pageFragments: channelsPageFragments,
    adminScopeOnly: true,
  },
];

export const SETTINGS_POSTS_MENU_ITEM: SettingsMenuItem = {
  label: 'SETTINGS.MENU.POSTS',
  route: 'posts',
};
